// /admin/documents — ЕДИНЫЙ ЦЕНТР ДОКУМЕНТОВ.
//
// Одно место, где оператор печати документов формирует и печатает ВСЕ
// документы по заказу одинаковыми карточками: паспорта изделий, упаковочный
// лист, протокол испытаний. Раньше кнопки были разбросаны (паспорта — в
// карточке заказа, протокол — отдельным пунктом меню). Здесь всё рядом и
// работает единообразно.
//
// Бэкенд не дублируется — переиспользуются существующие ручки:
//   паспорта/упак.лист → POST /manufacturing-orders/{id}/print-batch
//   упаковочный лист    → /admin/print/packing-list (форма с авто-раскладкой)
//   протокол испытаний  → /admin/print/test-reports (форма с автозаполнением)
//   документы партии    → /manufacturing-orders/{id}/documents/{kind} (виды —
//                          из описания заказа техпроцесса, поля — из шаблона)
//
// Право доступа — единое `document.print` (см. rbac.defaults.ts). На ручках оно
// принимается как альтернатива, поэтому одной роли хватает на весь центр.

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { DocumentCard } from "@/components/DocumentCard";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { StatusPill } from "@/components/StatusPill";
import { ApiError, manufacturingOrdersApi } from "@/lib/api";
import { hasModule } from "@/lib/auth";
import { useAuth } from "@/lib/AuthContext";
import { localized } from "@/lib/localized";
import type {
  ManufacturingOrderPrintBatchResponse,
  ManufacturingOrderRead,
  OrderDocumentDraft,
} from "@/lib/types";

const STATUS_LABEL: Record<string, string> = {
  DRAFT: "Черновик",
  READY: "Готов к запуску",
  IN_PRODUCTION: "В производстве",
  COMPLETED: "Завершён",
  CANCELLED: "Отменён",
};

export default function DocumentCenter() {
  return (
    <RequirePermission codes="document.print">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const navigate = useNavigate();
  const testReports = hasModule(useAuth().user, "ro-test-report");
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("order");
  const [search, setSearch] = useState("");

  const apiErr = (e: unknown, fallback: string) =>
    e instanceof ApiError
      ? `${e.code}: ${e.message}`
      : e instanceof Error
      ? e.message
      : fallback;

  // Список заказов для выбора (последние 100). Фильтруем на клиенте по
  // коду / артикулу / коду покупателя.
  const ordersList = useQuery({
    queryKey: ["manufacturing-orders", "doc-center"],
    queryFn: () => manufacturingOrdersApi.list({ page_size: 100 }),
  });

  const detail = useQuery({
    queryKey: ["manufacturing-order", selectedId],
    queryFn: () => manufacturingOrdersApi.get(selectedId!),
    enabled: !!selectedId,
  });
  const processes = useQuery({
    queryKey: ["manufacturing-orders", "processes"],
    queryFn: () => manufacturingOrdersApi.processes(),
    staleTime: 60_000,
  });

  const filtered = useMemo(() => {
    const items = ordersList.data?.items ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return items.slice(0, 20);
    return items
      .filter((o) =>
        [o.code, o.article, o.customer_code]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      )
      .slice(0, 20);
  }, [ordersList.data, search]);

  function selectOrder(id: string | null) {
    if (id) setParams({ order: id });
    else setParams({});
  }

  // ── действия по выбранному заказу ──
  const [docErr, setDocErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [copies, setCopies] = useState(1);

  const receive = useMutation({
    mutationFn: () => manufacturingOrdersApi.receive(selectedId!),
    onSuccess: () => {
      setDocErr(null);
      setMsg("Партия принята: изделия созданы по серийникам.");
      qc.invalidateQueries({ queryKey: ["manufacturing-order", selectedId] });
    },
    onError: (e) => setDocErr(apiErr(e, "Ошибка приёмки партии")),
  });

  const [printResult, setPrintResult] =
    useState<ManufacturingOrderPrintBatchResponse | null>(null);
  const printPassports = useMutation({
    mutationFn: () =>
      manufacturingOrdersApi.printBatch(selectedId!, {
        passports: true,
        packing_list: false,
        copies,
      }),
    onSuccess: (res) => {
      setDocErr(null);
      setPrintResult(res);
      setMsg(
        res.passport_package_job_id
          ? `Пакет паспортов сформирован: ${res.passport_jobs.length} стр. (единый PDF, одно задание печати).`
          : "Паспорта не сформированы.",
      );
    },
    onError: (e) => setDocErr(apiErr(e, "Ошибка постановки печати паспортов")),
  });

  const o = detail.data;
  const process = processes.data?.find((p) => p.key === o?.process_key);
  // Паспорта на партию — у техпроцессов с внешними серийниками (приёмка
  // партии создаёт изделия); иначе паспорта печатаются в ходе производства.
  const isOem = process?.order.serials.mode === "external";
  // Паспорта изделий и упаковочный лист — у техпроцессов с поштучной
  // нумерацией и упаковкой на паллеты; партии без них (манжеты) печатают
  // документы партии по шаблонам.
  const itemPassports = process ? process.order.serials.mode !== "none" : true;
  const packingList = process ? Boolean(process.order.packing) : true;
  const oemSerials = o?.external_serials ?? [];
  const receivedCount = oemSerials.filter((s) => s.item_id != null).length;
  const totalSerials = oemSerials.length;
  const allReceived = totalSerials > 0 && receivedCount === totalSerials;
  const launched =
    o?.status === "IN_PRODUCTION" || o?.status === "COMPLETED";
  const cancelled = o?.status === "CANCELLED";

  return (
    <>
      <PageHeader
        eyebrow="админ · документы"
        title="Центр документов"
        description="Все документы по заказу в одном месте: паспорта, упаковочный лист и протокол испытаний. Выберите заказ — карточки появятся ниже."
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/print/history" className="btn">
              История печати
            </Link>
            {testReports && (
              <Link to="/admin/print/test-reports/history" className="btn">
                История протоколов
              </Link>
            )}
          </div>
        }
      />

      {/* ── выбор заказа ── */}
      <section className="card p-4 mt-3 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="flex-1 min-w-[220px] px-3 py-2 rounded-md bg-surface border border-border text-sm"
            placeholder="Поиск заказа: код, артикул или код покупателя…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {selectedId && (
            <button className="btn" onClick={() => selectOrder(null)}>
              Сбросить выбор
            </button>
          )}
        </div>

        {ordersList.isLoading && <LoadingState />}
        {ordersList.isError && <ErrorState error={ordersList.error} />}

        {ordersList.data && (
          <div className="flex flex-wrap gap-2">
            {filtered.length === 0 && (
              <p className="text-sm text-text-muted">Заказы не найдены.</p>
            )}
            {filtered.map((ord) => (
              <OrderChip
                key={ord.id}
                order={ord}
                active={ord.id === selectedId}
                onClick={() => selectOrder(ord.id)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── карточки документов по выбранному заказу ── */}
      {!selectedId && (
        <div className="card p-8 mt-4 text-center text-text-muted">
          Выберите заказ выше, чтобы сформировать и напечатать его документы.
        </div>
      )}

      {selectedId && detail.isLoading && <LoadingState />}
      {selectedId && detail.isError && <ErrorState error={detail.error} />}

      {o && (
        <div className="mt-4 space-y-4">
          {/* шапка выбранного заказа */}
          <section className="card p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-text-muted">
                заказ на производство
              </p>
              <p className="text-lg font-mono mt-0.5">{o.code}</p>
              <p className="text-sm text-text-muted">
                {o.article ?? "—"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill kind="manufacturing" value={o.status} />
              <span className="text-xs text-text-muted">
                {STATUS_LABEL[o.status] ?? o.status}
              </span>
              <span className="pill text-xs">{process?.name ?? o.process_key}</span>
              <Link
                to={`/manufacturing-orders/${o.id}`}
                className="btn btn-sm"
                title="Открыть карточку заказа"
              >
                Карточка заказа →
              </Link>
            </div>
          </section>

          {docErr && (
            <ErrorBanner onDismiss={() => setDocErr(null)}>{docErr}</ErrorBanner>
          )}
          {msg && (
            <p className="card p-3 text-sm">{msg}</p>
          )}

          {/* контекстная подсказка по статусу — почему часть документов ещё
              недоступна и что сделать (как на карточке заказа) */}
          {!cancelled && isOem && !launched && (
            <div className="card p-3 text-sm text-text-secondary border-l-2 border-accent/50">
              {o.status === "DRAFT"
                ? "Заказ в черновике. Паспорта и упаковочный лист станут доступны после того, как заказ пометят готовым и запустят в производство (затем — приёмка партии). Протокол испытаний можно сформировать уже сейчас."
                : "Заказ готов, но ещё не запущен в производство. Запустите его (в карточке заказа), затем примите партию — после этого появятся паспорта и упаковочный лист. Протокол испытаний доступен уже сейчас."}
            </div>
          )}

          {cancelled ? (
            <div className="card p-6 text-center text-text-muted">
              Заказ отменён — документы недоступны.
            </div>
          ) : (
            <>
              {/* Документы партии техпроцесса — по шаблонам печати */}
              {(process?.order.documents ?? []).map((d) => (
                <BatchDocument
                  key={`${o.id}:${d.kind}`}
                  orderId={o.id}
                  kind={d.kind}
                  title={localized(d.label, d.kind)}
                  defaultCopies={d.copies_attr ? Number(o.attributes[d.copies_attr]) : undefined}
                />
              ))}

              {/* 1. Паспорта изделий */}
              {itemPassports && <DocumentCard
                icon="📄"
                title="Паспорта изделий"
                description={
                  isOem
                    ? "Один паспорт на каждое принятое изделие, единый многостраничный PDF."
                    : "Паспорта изделий этого техпроцесса печатаются в ходе производства, а не из шапки заказа."
                }
                muted={!isOem}
                status={
                  isOem ? (
                    <span className="pill text-xs">
                      принято {receivedCount} / {totalSerials}
                    </span>
                  ) : undefined
                }
              >
                {isOem ? (
                  <>
                    {!allReceived && o.status === "IN_PRODUCTION" && (
                      <button
                        className="btn"
                        onClick={() => {
                          setMsg(null);
                          setDocErr(null);
                          receive.mutate();
                        }}
                        disabled={receive.isPending || totalSerials === 0}
                        title="Создать изделия по внешним серийникам партии"
                      >
                        {receive.isPending ? "Принимаю…" : "Принять партию"}
                      </button>
                    )}
                    <label className="flex items-center gap-2 text-sm">
                      Копий:
                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={copies}
                        onChange={(e) =>
                          setCopies(
                            Math.max(1, Math.min(10, Number(e.target.value) || 1)),
                          )
                        }
                        className="w-16 px-2 py-1 rounded-md bg-surface border border-border text-sm font-mono"
                      />
                    </label>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setMsg(null);
                        setDocErr(null);
                        setPrintResult(null);
                        printPassports.mutate();
                      }}
                      disabled={
                        printPassports.isPending ||
                        !launched ||
                        receivedCount === 0
                      }
                      title={
                        !launched
                          ? "Запустите заказ в производство"
                          : receivedCount === 0
                          ? "Сначала примите партию"
                          : "Сформировать единый пакет паспортов и поставить в очередь печати"
                      }
                    >
                      {printPassports.isPending
                        ? "Ставлю в очередь…"
                        : "🖨 Печать пакета паспортов"}
                    </button>
                  </>
                ) : (
                  <span className="text-sm text-text-muted">
                    Печать — на стадиях производства (поэлементно).
                  </span>
                )}
                <Link to="/admin/print/history" className="btn btn-sm">
                  История
                </Link>
              </DocumentCard>}

              {printResult && printResult.errors.length > 0 && (
                <div className="card p-3 text-sm text-danger">
                  <p>Ошибки печати паспортов ({printResult.errors.length}):</p>
                  <ul className="list-disc ml-5">
                    {printResult.errors.map((er) => (
                      <li key={er.item_id} className="font-mono text-xs">
                        {er.item_id}: {er.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 2. Упаковочный лист */}
              {packingList && <DocumentCard
                icon="📦"
                title="Упаковочный лист"
                description="Один лист на партию с авто-раскладкой изделий на паллеты. Открывается формой — правится перед печатью."
                muted={!isOem}
              >
                <button
                  className="btn btn-primary"
                  onClick={() =>
                    navigate(
                      `/admin/print/packing-list?manufacturing_order_id=${o.id}`,
                    )
                  }
                  disabled={!isOem || !launched}
                  title={
                    !isOem
                      ? "Упаковочный лист доступен для заказов с серийниками партии"
                      : !launched
                      ? "Запустите заказ в производство и примите партию"
                      : "Открыть упаковочный лист, заполненный из этого заказа"
                  }
                >
                  Сформировать / открыть →
                </button>
              </DocumentCard>}

              {/* 3. Протокол испытаний */}
              {testReports && <DocumentCard
                icon="🧪"
                title="Протокол испытаний"
                description="Заполняется из заказа (артикул, параметры, серийники), остальное — вручную. Доступен для обоих типов заказа."
              >
                <button
                  className="btn btn-primary"
                  onClick={() =>
                    navigate(
                      `/admin/print/test-reports?manufacturing_order_id=${o.id}`,
                    )
                  }
                  title="Открыть форму протокола с автозаполнением из этого заказа"
                >
                  Сформировать / открыть →
                </button>
                <Link
                  to="/admin/print/test-reports/history"
                  className="btn btn-sm"
                >
                  История
                </Link>
              </DocumentCard>}
            </>
          )}
        </div>
      )}
    </>
  );
}

/** Документ партии по шаблону: форма открывается по кнопке, поля приходят
 *  заполненными из заказа, оператор правит и печатает. */
function BatchDocument({
  orderId,
  kind,
  title,
  defaultCopies,
}: {
  orderId: string;
  kind: string;
  title: string;
  /** Копий по умолчанию — значение атрибута заказа (copies_attr техпроцесса). */
  defaultCopies?: number;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const draft = useQuery({
    queryKey: ["order-document", orderId, kind],
    queryFn: () => manufacturingOrdersApi.documentDraft(orderId, kind),
    enabled: open,
    retry: false,
  });
  const noTemplate = draft.error instanceof ApiError && draft.error.status === 404;
  return (
    <DocumentCard
      icon="📋"
      title={title}
      description={t("batchDocs.description")}
      status={
        draft.data ? <span className="pill text-xs">{draft.data.template_name}</span> : undefined
      }
    >
      <button className="btn btn-primary" onClick={() => setOpen(!open)}>
        {open ? t("batchDocs.close") : t("batchDocs.open")}
      </button>
      <Link to="/admin/print/history" className="btn btn-sm">
        {t("batchDocs.history")}
      </Link>
      {open && (
        <div className="w-full">
          {draft.isLoading && <LoadingState />}
          {noTemplate ? (
            <ErrorBanner>{t("batchDocs.noTemplate")}</ErrorBanner>
          ) : (
            draft.isError && <ErrorState error={draft.error} />
          )}
          {draft.data && (
            <BatchDocumentForm
              key={draft.data.template_id}
              orderId={orderId}
              draft={draft.data}
              defaultCopies={defaultCopies}
            />
          )}
        </div>
      )}
    </DocumentCard>
  );
}

const JOURNAL_SOURCE = /^(sign|meas|batch)\./;

function BatchDocumentForm({
  orderId,
  draft,
  defaultCopies,
}: {
  orderId: string;
  draft: OrderDocumentDraft;
  defaultCopies?: number;
}) {
  const { t } = useTranslation();
  // Поля из журналов партии печатаются как записаны — в форму их не берём.
  const editable = draft.fields.filter((f) => !f.readonly);
  const journal = draft.fields.filter((f) => f.readonly);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(editable.map((f) => [f.key, f.value])),
  );
  // Больше 20 копий за одно задание не печатается — остаток ставят следующим.
  const [copies, setCopies] = useState(() =>
    Math.max(1, Math.min(20, Math.ceil(defaultCopies ?? 1) || 1)),
  );
  const print = useMutation({
    mutationFn: () =>
      manufacturingOrdersApi.printDocument(orderId, draft.kind, { values, copies }),
  });
  return (
    <form
      className="space-y-3 pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        print.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {editable.map((f) => (
          <label key={f.key} className="block text-sm">
            <span className="text-text-secondary">
              {f.label}
              {f.required && " *"}
            </span>
            <input
              className="w-full mt-1 px-3 py-2 rounded-md bg-surface border border-border text-sm"
              value={values[f.key] ?? ""}
              required={f.required}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
            />
            {f.source && (
              <span className="text-xs text-text-muted" title={f.source}>
                {JOURNAL_SOURCE.test(f.source)
                  ? t("batchDocs.journalEmpty")
                  : t("batchDocs.fromOrder")}
              </span>
            )}
          </label>
        ))}
      </div>
      {journal.length > 0 && (
        <details className="rounded-md border border-border p-3">
          <summary className="cursor-pointer text-sm text-text-secondary">
            {t("batchDocs.journal", { count: journal.length })}
          </summary>
          <p className="text-xs text-text-muted mt-2">{t("batchDocs.journalHint")}</p>
          <div className="grid gap-3 sm:grid-cols-2 mt-2">
            {journal.map((f) => (
              <label key={f.key} className="block text-sm">
                <span className="text-text-secondary">{f.label}</span>
                <input
                  className="w-full mt-1 px-3 py-2 rounded-md bg-surface border border-border text-sm text-text-muted"
                  value={f.value}
                  readOnly
                  title={f.source ?? undefined}
                />
              </label>
            ))}
          </div>
        </details>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-sm">
          {t("batchDocs.copies")}
          <input
            type="number"
            min={1}
            max={20}
            value={copies}
            onChange={(e) => setCopies(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
            className="w-16 px-2 py-1 rounded-md bg-surface border border-border text-sm font-mono"
          />
        </label>
        {defaultCopies !== undefined && defaultCopies > 20 && (
          <span className="text-xs text-text-muted">
            {t("batchDocs.copiesOver", { count: Math.ceil(defaultCopies) })}
          </span>
        )}
        <button type="submit" className="btn btn-primary" disabled={print.isPending}>
          {print.isPending ? t("batchDocs.printing") : t("batchDocs.print")}
        </button>
      </div>
      {print.isError && (
        <ErrorBanner>
          {print.error instanceof Error ? print.error.message : t("batchDocs.printFailed")}
        </ErrorBanner>
      )}
      {print.data && (
        <p className="text-sm">
          {t("batchDocs.enqueued", { job: print.data.job_id })}{" "}
          <Link to="/admin/print/history" className="underline">
            {t("batchDocs.history")}
          </Link>
        </p>
      )}
    </form>
  );
}

function OrderChip({
  order,
  active,
  onClick,
}: {
  order: ManufacturingOrderRead;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md border text-sm text-left transition-colors ${
        active
          ? "border-accent bg-accent/10 text-accent-strong"
          : "border-border bg-surface hover:bg-surface-hover"
      }`}
      title={order.article ?? order.code}
    >
      <span className="font-mono">{order.code}</span>
      {order.article && (
        <span className="text-text-muted"> · {order.article}</span>
      )}
    </button>
  );
}
